<template>
  <a-modal v-model="visible" :title="'Manage Schedule: ' + (record ? record.name : '')" :footer="null" width="700px">
    <div v-if="record" class="mb-4">
      <small class="text-muted">Default Timing: {{ record.default_start_time }} - {{ record.default_end_time }}</small>
    </div>

    <a-tabs default-active-key="1">
      <a-tab-pane key="1" tab="Add/Update Override">
        <a-form :form="form" @submit="handleSubmit">
          <a-form-item label="Select Date">
            <a-date-picker
              style="width: 100%"
              v-decorator="['date', { rules: [{ required: true, message: 'Please select date' }] }]"
            />
          </a-form-item>

          <a-form-item label="Action">
            <a-radio-group v-decorator="['is_cancelled', { initialValue: 0 }]" @change="e => isCancelled = e.target.value">
              <a-radio :value="0">Active (Change Timing)</a-radio>
              <a-radio :value="1">Cancel Event for this day</a-radio>
            </a-radio-group>
          </a-form-item>

          <div v-if="!isCancelled" class="row">
            <div class="col-md-6 col-12">
              <a-form-item label="Start Time">
                <a-time-picker
                  format="HH:mm"
                  style="width: 100%"
                  v-decorator="['start_time', { rules: [{ required: !isCancelled, message: 'Please select start time' }] }]"
                />
              </a-form-item>
            </div>
            <div class="col-md-6 col-12">
              <a-form-item label="End Time">
                <a-time-picker
                  format="HH:mm"
                  style="width: 100%"
                  v-decorator="['end_time', { rules: [{ required: !isCancelled, message: 'Please select end time' }] }]"
                />
              </a-form-item>
            </div>
          </div>

          <a-form-item label="Remark (Optional)">
            <a-textarea
              placeholder="e.g. Sunday late wake-up"
              v-decorator="['remark']"
            />
          </a-form-item>

          <div class="text-right mt-4">
            <a-button class="mr-2" @click="handleCancel">Close</a-button>
            <a-button type="primary" html-type="submit" :loading="loading">Save Schedule</a-button>
          </div>
        </a-form>
      </a-tab-pane>

      <a-tab-pane key="2" tab="Existing Overrides">
        <a-table
          :columns="scheduleColumns"
          :dataSource="schedules"
          :rowKey="record => record.id"
          size="small"
          :loading="loadingSchedules"
        >
          <template slot="is_cancelled" slot-scope="text">
            <span v-if="text" class="badge badge-danger">Cancelled</span>
            <span v-else class="badge badge-success">Active</span>
          </template>
          <template slot="timing" slot-scope="text, record">
            <span v-if="!record.is_cancelled">{{ record.start_time }} - {{ record.end_time }}</span>
            <span v-else>-</span>
          </template>
          <template slot="action" slot-scope="text, record">
            <a-popconfirm
              title="Are you sure you want to delete this override?"
              @confirm="deleteSchedule(record.id)"
              ok-text="Yes"
              cancel-text="No"
            >
              <a href="javascript:;" class="btn btn-sm btn-danger"><i class="fe fe-trash" /></a>
            </a-popconfirm>
          </template>
        </a-table>
      </a-tab-pane>
    </a-tabs>
  </a-modal>
</template>

<script>
import moment from 'moment'

const scheduleColumns = [
  { title: 'Date', dataIndex: 'date', key: 'date' },
  { title: 'Status', dataIndex: 'is_cancelled', key: 'is_cancelled', scopedSlots: { customRender: 'is_cancelled' } },
  { title: 'Timing', key: 'timing', scopedSlots: { customRender: 'timing' } },
  { title: 'Remark', dataIndex: 'remark', key: 'remark' },
  { title: '', key: 'action', scopedSlots: { customRender: 'action' }, width: 50 },
]

export default {
  name: 'HostelEventScheduleModal',
  data() {
    return {
      form: this.$form.createForm(this),
      loading: false,
      loadingSchedules: false,
      visible: false,
      record: null,
      isCancelled: 0,
      schedules: [],
      scheduleColumns,
    }
  },
  mounted() {
    this.$eventBus.$on('openHostelEventSchedule', record => {
      this.record = record
      this.visible = true
      this.isCancelled = 0
      this.fetchSchedules()
      this.$nextTick(() => {
        this.form.setFieldsValue({
          start_time: moment(record.default_start_time, 'HH:mm:ss'),
          end_time: moment(record.default_end_time, 'HH:mm:ss'),
          is_cancelled: 0,
        })
      })
    })
  },
  beforeDestroy() {
    this.$eventBus.$off('openHostelEventSchedule')
  },
  methods: {
    fetchSchedules() {
      if (!this.record) return
      this.loadingSchedules = true
      this.$http.get(`/api/hostel/event/${this.record.id}/schedule`)
        .then(({ data }) => {
          this.schedules = data.data
        })
        .finally(() => {
          this.loadingSchedules = false
        })
    },
    deleteSchedule(scheduleId) {
      this.loadingSchedules = true
      this.$http.delete(`/api/hostel/event/${this.record.id}/schedule/${scheduleId}`)
        .then(() => {
          this.$notification.success({ message: 'Deleted', description: 'Schedule override removed.' })
          this.fetchSchedules()
        })
        .catch(error => {
          this.$neoNotify.error(error)
        })
        .finally(() => {
          this.loadingSchedules = false
        })
    },
    handleCancel() {
      this.visible = false
      this.form.resetFields()
    },
    handleSubmit(e) {
      e.preventDefault()
      this.form.validateFields((err, values) => {
        if (!err) {
          this.loading = true
          const payload = {
            date: values.date.format('YYYY-MM-DD'),
            is_cancelled: values.is_cancelled,
            remark: values.remark,
          }

          if (!values.is_cancelled) {
            payload.start_time = values.start_time.format('HH:mm')
            payload.end_time = values.end_time.format('HH:mm')
          }

          this.$http.post(`/api/hostel/event/${this.record.id}/schedule`, payload)
            .then(() => {
              this.loading = false
              this.$emit('onScheduled')
              this.fetchSchedules()
              this.form.resetFields()
              this.$notification.success({
                message: 'Success',
                description: 'Schedule override saved successfully.',
              })
            })
            .catch(error => {
              this.loading = false
              this.$neoNotify.error(error)
            })
        }
      })
    },
  },
}
</script>
